"use client";
import { useContext, useCallback, useEffect, useState } from "react";
import { LocaleAttribute } from "@/app/i18n/settings";
import { getBaseUrl } from "@/util/get-base-url";
import { formatCurrencyFormat } from "@/util/formatCurrencyFormat";
import { toPriceString } from "@/util/toPriceString";
import { CUSTOMER_LEVEL } from "@prisma/client";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import CollectionListItemClient from "@/components/collections/collection-list-favorite-client"
import "./product-showboxes.css";
import ProductTagRenderer from "./product-tag-renderer";
import { useProductLayout } from "@/lib/providers/product-layout-provider";
import { borderRadiusShorthand, useLayoutStyle } from "@/lib/providers/layout-style-provider";
import CollectionListItem, { ProductCardConfig } from "../components/collections/collection-list-item";

interface ProductShowboxesCarouselProps {
    productGroupItems: any;
    productList: any[];
    priorityImage: boolean;
    colorStyle: any;
    loc: LocaleAttribute;
    shopCurrency?: any;
    level: CUSTOMER_LEVEL;
    text: any;
    groupId: number;
    radiusStyle: any;
}

const ProductShowboxesCarousel = (props: ProductShowboxesCarouselProps) => {
    const { productGroupItems, productList, priorityImage, colorStyle, loc, shopCurrency, level, text, groupId, radiusStyle } = props;
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, dragFree: true });

    const [prevDisabled, setPrevDisabled] = useState(true);
    const [nextDisabled, setNextDisabled] = useState(true);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setPrevDisabled(!emblaApi.canScrollPrev());
        setNextDisabled(!emblaApi.canScrollNext());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        onSelect();
        emblaApi.on('select', onSelect);
        emblaApi.on('reInit', onSelect);
    }, [emblaApi, onSelect]);

    const scrollNext = () => {
        if (emblaApi) emblaApi.scrollNext();
    };
    const scrollPrev = () => {
        if (emblaApi) emblaApi.scrollPrev();
    };

    const productLayout = useProductLayout();
    const layoutStyle = useLayoutStyle();
    
    const maxItem = productLayout.productColumns
    const isGradientCard = productLayout.productCardType === "TYPE_2";
    const resolvedMobileColumn = productLayout.mobileColumn ?? 2;
    const useTightMobileGap = isGradientCard && resolvedMobileColumn === 3;

    return (

            <div id="product-showboxes-component-carousel-container" className="w-full relative">
                <div id="product-showboxes-component-carousel-embla" className="embla" ref={emblaRef}>
                    <div
                        id="product-showboxes-component-carousel-embla-container"
                        className={`embla__container product-showboxes-carousel-embla${useTightMobileGap ? " product-showboxes-gap-tight" : ""}`}
                        style={{ display: "flex", ...(useTightMobileGap ? { "--mobile-gap": "8px" } : {}) } as React.CSSProperties}
                    >
                        {productList.map((product, index) => (
                            (product && product.productVariant && product.productVariant.length > 0) && (
                            <div
                                id={`product-showboxes-component-carousel-item-${product.id}`}
                                key={product.id}
                                className={`${maxItem == 6 ? "product-showboxes-6" : "product-showboxes"}${isGradientCard ? " product-showboxes-mobile-custom" : ""} flex flex-col gap-5 flex-shrink-0`}
                                style={isGradientCard ? ({ "--mobile-columns": resolvedMobileColumn } as React.CSSProperties) : undefined}
                            >
                                <CollectionListItem
                                    product={product}
                                    key={product.id}
                                    href={`/products/${product.handle}`}
                                    level={level}
                                    priorityImage={index < 2}
                                    shopCurrency={shopCurrency}
                                    cardType={productLayout.productCardType == "TYPE_1" ? ProductCardConfig.DEFAULT : 
                                        productLayout.productCardType == "TYPE_2" ? ProductCardConfig.GRADIENT : 
                                        productLayout.productCardType == "TYPE_3" ? ProductCardConfig.BUTTON_BESIDE_PRICE :
                                        productLayout.productCardType == "TYPE_5" ? ProductCardConfig.BORDER_GRADIENT_SHORT_DESC :
                                        ProductCardConfig.DEFAULT}
                                />
                            </div>
                            )
                        ))}
                    </div>
                </div>
                <div id={`product-showboxes-component-carousel-item-actions`} className="w-full flex justify-between items-center">
                    {productList.length > maxItem && (<div className="flex w-1/3 justify-end mt-[40px]"></div>)}
                    {(productGroupItems[groupId].buttonAttributes 
                        && productGroupItems[groupId].enableButton
                    ) && (
                        <div id={`product-showboxes-component-carousel-item-button`} className="w-full flex justify-center mt-[40px]">
                            <a href={productGroupItems[groupId].buttonAttributes.linkUrl || ""} aria-label="button">
                                <button
                                    className="min-w-12 min-h-12 w-fit h-fit py-4 px-10 rounded-full font-medium px-10 dynamic-bg dynamic-color"
                                    aria-label="Link Button"
                                    style={
                                        {
                                            "--dynamic-bg": colorStyle?.buttonBgNormal || "#006ef2",
                                            "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                                            "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                            "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                                            "--dynamic-focus-color": colorStyle?.buttonTextHover || "#ffffff",
                                            borderTopLeftRadius: radiusStyle?.buttonRadiusTopLeft ?? "6px",
                                            borderTopRightRadius: radiusStyle?.buttonRadiusTopRight ?? "6px",
                                            borderBottomLeftRadius: radiusStyle?.buttonRadiusBottomLeft ?? "6px",
                                            borderBottomRightRadius: radiusStyle?.buttonRadiusBottomRight ?? "6px",
                                        } as React.CSSProperties
                                    }>
                                    {productGroupItems[groupId].buttonAttributes.displayName == "" ?  text?.see_all_button : productGroupItems[groupId].buttonAttributes.displayName}
                                </button>
                            </a>
                        </div>
                    )}   
                    {productList.length > maxItem && (
                    <div id="product-showboxes-component-carousel-prev-next" className="flex w-1/3 justify-center mt-[40px]">
                        <button
                            aria-label="Previous" 
                            className={`
                                embla__button embla__button--prev inline-flex items-center justify-center 
                                whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none 
                                focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none 
                                disabled:opacity-50 text-base font-normal border bg-transparent h-10 w-10 rounded-full max-md:hidden 
                                dynamic-bg dynamic-color dynamic-border ${productLayout.arrowPosition == "SIDE" ? "absolute left-0 top-1/2 -translate-y-[170%] -translate-x-1/2 shadow" : "relative transform left-0 top-0 translate-y-0 mx-1" }`}
                            style={
                                {
                                    "--dynamic-bg": productLayout.arrowPosition == "SIDE" ? "#ffffff" : (colorStyle?.buttonBgNormal || "#009eca"),
                                    "--dynamic-hover-bg": productLayout.arrowPosition == "SIDE" ? "#ffffff" : colorStyle?.buttonBgHover || "#254b7c",
                                    "--dynamic-border": productLayout.arrowPosition == "SIDE" ? "#B3B3B3" : colorStyle?.buttonTextNormal || "#ffffff",
                                    "--dynamic-hover-border": productLayout.arrowPosition == "SIDE" ? "#FFC20E" : colorStyle?.buttonTextHover || "#ffffff",
                                    "--dynamic-color": productLayout.arrowPosition == "SIDE" ? "#5B595A" : colorStyle?.buttonTextNormal || "#ffffff",
                                    "--dynamic-hover-color": productLayout.arrowPosition == "SIDE" ? "#5B595A" : colorStyle?.buttonTextHover || "#ffffff",
                                    "--dynamic-focus-color": productLayout.arrowPosition == "SIDE" ? "#5B595A" : colorStyle?.buttonTextNormal || "#ffffff",
                                    "--dynamic-hover-focus-color": productLayout.arrowPosition == "SIDE" ? "#5B595A" : colorStyle?.buttonTextHover || "#ffffff",
                                } as React.CSSProperties
                            }
                            onClick={scrollPrev}
                            disabled={prevDisabled}
                        >
                            <ChevronLeft 
                                className="h-5 w-5"
                            />
                        </button>
                        <button
                            aria-label="Next"
                            className={`
                                embla__button embla__button--next inline-flex items-center justify-center 
                                whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none 
                                focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none 
                                disabled:opacity-50 text-base font-normal border bg-transparent h-10 w-10 rounded-full -right-12 max-md:hidden 
                                dynamic-bg dynamic-color dynamic-border ${productLayout.arrowPosition == "SIDE" ? "absolute right-0 top-1/2 -translate-y-[170%] translate-x-1/2 shadow" : "relative transform left-0 top-0 translate-y-0 mx-1"}`}
                            style={
                                {
                                    "--dynamic-bg": productLayout.arrowPosition == "SIDE" ? "#ffffff" : (colorStyle?.buttonBgNormal || "#009eca"),
                                    "--dynamic-hover-bg": productLayout.arrowPosition == "SIDE" ? "#ffffff" : colorStyle?.buttonBgHover || "#254b7c",
                                    "--dynamic-border": productLayout.arrowPosition == "SIDE" ? "#B3B3B3" : colorStyle?.buttonTextNormal || "#ffffff",
                                    "--dynamic-hover-border": productLayout.arrowPosition == "SIDE" ? "#FFC20E" : colorStyle?.buttonTextHover || "#ffffff",
                                    "--dynamic-color": productLayout.arrowPosition == "SIDE" ? "#5B595A" : colorStyle?.buttonTextNormal || "#ffffff",
                                    "--dynamic-hover-color": productLayout.arrowPosition == "SIDE" ? "#5B595A" : colorStyle?.buttonTextHover || "#ffffff",
                                    "--dynamic-focus-color": productLayout.arrowPosition == "SIDE" ? "#5B595A" : colorStyle?.buttonTextNormal || "#ffffff",
                                    "--dynamic-hover-focus-color": productLayout.arrowPosition == "SIDE" ? "#5B595A" : colorStyle?.buttonTextHover || "#ffffff",
                                } as React.CSSProperties
                            }
                            onClick={scrollNext}
                            disabled={nextDisabled}
                        >
                            <ChevronRight 
                                className="h-5 w-5"
                            />
                        </button>
                    </div>
                    )}
                </div>
            </div>
    )
}

export default ProductShowboxesCarousel;